// web/src/components/shops/analytics/SendCompModal.tsx
//
// Confirmation modal triggered from AtRiskList. Posts to /api/gamification
// action=comp-send and surfaces friendly errors.

import { useState } from 'react';
import { X } from 'lucide-react';

interface Props {
  shopId: string;
  customerAddress: string;
  customerName?: string | null;
  authToken: string;
  onClose: () => void;
  onSent?: (code: string) => void;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; code: string }
  | { kind: 'error'; message: string };

export function SendCompModal({ shopId, customerAddress, customerName, authToken, onClose, onSent }: Props) {
  const [message, setMessage] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const apiBase = (import.meta.env.VITE_API_BASE_PATH as string | undefined) || '/api';

  async function submit() {
    setPhase({ kind: 'sending' });
    try {
      const res = await fetch(`${apiBase}/gamification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ action: 'comp-send', shopId, customerAddress, message: message || null }),
      });
      const body = await res.json().catch(() => ({} as any));
      if (res.ok) {
        setPhase({ kind: 'sent', code: body.code });
        onSent?.(body.code);
        return;
      }
      const friendly = res.status === 409 && body.error === 'comp_already_pending'
        ? `${customerName || 'This customer'} already has a pending free coffee from this shop. Wait until it's redeemed or expires.`
        : body.error === 'customer_no_email'
          ? 'No email on file for this customer. Ask them to set one in their profile.'
          : body.error || 'Send failed. Try again.';
      setPhase({ kind: 'error', message: friendly });
    } catch (e: any) {
      setPhase({ kind: 'error', message: e?.message || 'Network error. Try again.' });
    }
  }

  if (phase.kind === 'sent') {
    return (
      <div role="dialog" aria-label="Free coffee sent" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-[16px] p-6 max-w-sm w-full">
          <h2 className="font-grotesk font-bold text-xl text-primary mb-3">Free coffee sent</h2>
          <p className="font-grotesk text-[13px] text-body-grey mb-2">Code:</p>
          <code className="block font-mono text-lg text-primary bg-background rounded-[10px] p-3 mb-4 text-center tracking-widest">
            {phase.code}
          </code>
          <p className="font-grotesk text-[12px] text-body-grey mb-4">
            {customerName || 'They'} will see it in their email and on their dashboard.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-label="Send free coffee" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm safe-modal">
      <div className="bg-white rounded-[16px] p-4 sm:p-6 max-w-sm w-full max-h-full overflow-y-auto">
        <div className="flex items-start justify-between mb-3 gap-3">
          <h2 className="font-grotesk font-bold text-lg text-primary">
            Send a free coffee to {customerName || 'this customer'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={phase.kind === 'sending'}
            aria-label="Close"
            className="p-3 -m-2 text-body-grey hover:text-primary flex-none"
          >
            <X size={16} />
          </button>
        </div>
        <p className="font-grotesk text-[12px] text-body-grey mb-4">
          They'll get an email + push notification with a code to show on their next visit.
        </p>
        <label className="block mb-4">
          <span className="block font-heading text-[8px] text-body-grey mb-1.5">
            OPTIONAL NOTE (SHOWN IN THE EMAIL)
          </span>
          <textarea
            value={message}
            maxLength={140}
            disabled={phase.kind === 'sending'}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Missed you! Coffee on us."
            className="w-full bg-background rounded-[8px] border-2 border-hairline text-primary p-2.5 text-base font-grotesk min-h-[60px] focus:border-coral outline-none"
          />
        </label>
        {phase.kind === 'error' && (
          <p role="alert" className="font-grotesk text-[12px] text-error mb-3">{phase.message}</p>
        )}
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={phase.kind === 'sending'}
            className="flex-1 rounded-full border-2 border-hairline text-body-grey font-grotesk font-bold text-sm py-3"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={phase.kind === 'sending'}
            className="flex-1 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3"
          >
            {phase.kind === 'sending' ? 'Sending…' : 'Send free coffee'}
          </button>
        </div>
      </div>
    </div>
  );
}
